import { type ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { IconArrowLeft } from '@rossum/ui/icons/tabler';
import {
  IconButton,
  Skeleton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { useNavBarVisibility } from '../../../components/NavBar/hooks/useNavBarVisibility';
import { SearchButton } from '../../../components/NavBar/SearchButton';
import PoweredBy from '../../../components/UI/PoweredBy';
import { isEmbedded } from '../../../constants/config';
import CallToActionButton from '../../../containers/CallToAction';
import { DocumentTopBar } from '../../document-topbar';
import { useQuickSearchContext } from '../../quick-search/QuickSearchContext';
import { UserPanel } from '../../user-panel';
import { useIsTrial } from '../hooks/useOrganizationFlags';

type SplitTopBarProps = {
  documentName: string | null | undefined;
  loading?: boolean;
  onLeave: () => void;
  pageNavigation?: ReactNode;
};

export const SplitTopBar = ({
  documentName,
  loading = false,
  onLeave,
  pageNavigation,
}: SplitTopBarProps) => {
  const intl = useIntl();

  const isTrial = useIsTrial();

  const { isVisible: isUserPanelVisible } = useNavBarVisibility();

  const { setOpen } = useQuickSearchContext();

  const openQuickSearch = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const start = (
    <>
      <IconButton
        size="medium"
        color="secondary"
        data-cy="exit-route-button"
        onClick={onLeave}
      >
        <SvgIcon fontSize="medium">
          <IconArrowLeft />
        </SvgIcon>
      </IconButton>
      <Stack sx={{ minWidth: 0 }}>
        {loading || !documentName ? (
          <Skeleton width={200} data-cy="split-topbar-document-name-skeleton" />
        ) : (
          <Tooltip title={documentName}>
            <Typography variant="h6" noWrap>
              {intl.formatMessage(
                { id: 'containers.editDocument.editingDocumentName' },
                { documentName }
              )}
            </Typography>
          </Tooltip>
        )}
      </Stack>
    </>
  );

  const end = (
    <>
      {!isEmbedded() && <SearchButton onClick={openQuickSearch} />}
      {isTrial && <CallToActionButton ctaLocation="navigation-panel" />}
      <PoweredBy />
      {isUserPanelVisible ? <UserPanel /> : null}
    </>
  );

  return (
    <DocumentTopBar
      start={start}
      // The translations toggle lands with MAT-1967 (S10).
      center={pageNavigation ?? null}
      end={end}
    />
  );
};
